import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import Modal from 'react-modal';
import ProcedureTable from "../components/ProcedureTable";
import { buildApiUrl } from '../utils/api';
import {
  setSelectedColumns,
  setData,
  fetchSavedTables,
  setParameters,
  saveColumns,
  deleteSavedTable,
  importarTablas,
  fetchInitialData,
  setDataForProcedure
} from '../redux/columnsSlice';

const SelectorBD = () => {
  const dispatch = useDispatch();

  // Estado en Redux
  const selectedColumns = useSelector((state) => state.columns.selectedColumns || {});
  const data = useSelector((state) => state.columns.data || {});
  const savedTables = useSelector((state) => state.columns.savedTables || []);
  const savedTablesLoading = useSelector((state) => state.columns.savedTablesLoading);
  const savedTablesError = useSelector((state) => state.columns.savedTablesError);

  // Estado local
  const [databases, setDatabases] = useState([]);
  const [selectedDatabase, setSelectedDatabase] = useState('');
  const [procedures, setProcedures] = useState([]);
  const [selectedProcedures, setSelectedProcedures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectionName, setSelectionName] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProcedureId, setEditingProcedureId] = useState(null);
  const [uniqueRowId, setUniqueRowId] = useState('');
  const [connectionInfo, setConnectionInfo] = useState({});
  const [selectedProcedure, setSelectedProcedure] = useState('');

  // Configuración del modal
  useEffect(() => {
    const appElement = document.getElementById('autocrm-root');
    if (appElement) {
      Modal.setAppElement(appElement);
    } else {
      console.error('No se encontró el elemento #autocrm-root para Modal.setAppElement()');
    }
  }, []);

  // Obtener bases de datos disponibles
  useEffect(() => {
    axios
      .get(buildApiUrl('/databases'), {
        headers: { 'X-WP-Nonce': autocrm_globals.nonce },
      })
      .then((response) => {
        setDatabases(Array.isArray(response.data) ? response.data : []);
      })
      .catch((err) => console.error('Error al obtener bases de datos:', err));
  }, []);

  /**
   * useEffect para asegurar que "order_id" esté presente y evitar duplicados en cada procedimiento.
   * Se añade "order_id" al inicio del array de columnas para cada procedimiento seleccionado.
   */
  useEffect(() => {
    if (selectedProcedures.length > 0) {
      const updatedColumns = { ...selectedColumns };
      selectedProcedures.forEach((proc) => {
        updatedColumns[proc] = [...new Set(["order_id", ...(updatedColumns[proc] || [])])];
      });
      dispatch(setSelectedColumns(updatedColumns));
    }
  }, [selectedProcedures, dispatch]);

  /**
   * Función para manejar la selección/deselección de columnas.
   * Bloquea la acción si se intenta deseleccionar "order_id".
   */
  const handleColumnSelection = (procedure, column) => {
    if (column === "order_id") return;
    dispatch(
      setSelectedColumns({
        procedure,
        columns: selectedColumns[procedure].includes(column)
          ? selectedColumns[procedure].filter((col) => col !== column)
          : [...selectedColumns[procedure], column],
      })
    );
  };

  /**
   * Obtener procedimientos almacenados para la base de datos seleccionada.
   */
  const fetchProcedures = (database) => {
    setLoading(true);
    axios
      .get(buildApiUrl('/procedures'), {
        params: { database },
        headers: { 'X-WP-Nonce': autocrm_globals.nonce },
      })
      .then((response) => {
        const proceduresList = Array.isArray(response.data) ? response.data : [];
        setProcedures(proceduresList);
      })
      .catch((err) => {
        console.error('Error al obtener procedimientos:', err);
        setProcedures([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /**
   * Manejar cambio de base de datos en <select>
   */
  const handleDatabaseChange = (e) => {
    const db = e.target.value;
    setSelectedDatabase(db);
    setProcedures([]);
    setSelectedProcedures([]);
    dispatch(setSelectedColumns({}));
    dispatch(setData({}));
    dispatch(
      setParameters({
        fechaInicio: '2024-04-01',
        fechaFin: '2100-12-31',
        perPage: 10,
        page: 1,
        p_column_filter: '',
        p_field_filter: ''
      })
    );
    setEditingProcedureId(null);
    setSelectionName('');
    setConnectionInfo({});
    setUniqueRowId('');
    setSelectedProcedure('');

    if (db) {
      fetchProcedures(db);
    }
  };

  /**
   * Agregar un procedimiento a la lista de seleccionados y cargar datos iniciales.
   */
  const handleAddProcedure = () => {
    if (!selectedDatabase || !selectedProcedure || selectedProcedures.includes(selectedProcedure)) {
      alert('Seleccione una base de datos y un procedimiento válido.');
      return;
    }
    setSelectedProcedures((prev) => [...prev, selectedProcedure]);
    dispatch(
      fetchInitialData({
        database: selectedDatabase,
        procedure: selectedProcedure,
        params: {
          fechaInicio: '2024-04-01',
          fechaFin: '2100-12-31',
          perPage: 10,
          page: 1,
          p_column_filter: '',
          p_field_filter: ''
        },
      })
    );
    setSelectedProcedure('');
  };

  /**
   * Ejecutar procedimientos seleccionados en la primera fase para ver datos.
   */
  const fetchCombinedData = async () => {
    if (selectedProcedures.length === 0) {
      alert('Seleccione al menos un procedimiento para ejecutar.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      for (const procedure of selectedProcedures) {
        await dispatch(
          fetchInitialData({
            database: selectedDatabase,
            procedure,
            params: {
              fechaInicio: '2024-04-01',
              fechaFin: '2100-12-31',
              perPage: 10,
              page: 1,
              p_column_filter: '',
              p_field_filter: ''
            },
          })
        ).unwrap();
      }
    } catch (err) {
      setError('Error al ejecutar los procedimientos: ' + err);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Ejecutar procedimientos de un grupo guardado (fusión SP + constructor_defined).
   * Esta función recorre los procedimientos y dispara la acción fetchInitialData para cada uno.
   */
  const executeSavedGroup = (procedures, database, group) => {
    procedures.forEach((proc) => {
      dispatch(
        fetchInitialData({
          unique_key: group.unique_key, // se usa el unique_key del grupo guardado
          database,
          procedure: proc,
          params: {
            fechaInicio: '2024-04-01',
            fechaFin: '2100-12-31',
            perPage: 10,
            page: 1,
            p_column_filter: '',
            p_field_filter: ''
          },
        })
      )
        .unwrap()
        .then((procData) => {
          dispatch(
            setDataForProcedure({
              procedure: proc,
              columns: procData.columns,
              data: procData.data,
            })
          );
        })
        .catch((error) => {
          console.error(`Error al ejecutar el procedimiento ${proc}:`, error);
        });
    });
  };

  /**
   * Ejecutar un procedimiento específico.
   */
  const executeProcedure = (procedure, database) => {
    const params = {
      fechaInicio: '2024-04-01',
      fechaFin: '2100-12-31',
      perPage: 10,
      page: 1,
      p_column_filter: '',
      p_field_filter: ''
    };

    dispatch(
      fetchInitialData({
        database,
        procedure,
        params,
      })
    )
      .unwrap()
      .then((procData) => {
        dispatch(setDataForProcedure({
          procedure: procData.procedure,
          columns: procData.columns,
          data: procData.data,
        }));
      })
      .catch((error) => {
        console.error(`Error al ejecutar el procedimiento ${procedure}:`, error);
      });
  };

  /**
   * Manejar clic en un grupo guardado de la tabla "Tablas Guardadas".
   * Se fusionan las columnas del procedimiento con las del constructor (constructor_defined),
   * asegurando que "order_id" siempre esté presente.
   */
  const handleSavedProcedureClick = (group) => {
    if (!group || !group.columns) {
      console.error("ERROR: 'group' está indefinido o no tiene 'columns'. Se aborta.");
      return;
    }
    console.log("handleSavedProcedureClick -> group:", group);
    dispatch(setSelectedColumns({}));
    dispatch(setData({}));
    const realProcedures = (group.procedures || []).filter(
      (proc) => proc !== 'constructor_defined'
    );
    setSelectedDatabase(group.database);
    setSelectedProcedures(realProcedures);
    setEditingProcedureId(group.unique_key);
    setSelectionName(group.group_name || '');
    setUniqueRowId(group.unique_row_id || '');
    setConnectionInfo(group.connectionInfo || {});
    setSelectedProcedure('');
    const constructorCols = (group?.columns || [])
      .filter((col) => col.procedure_name === 'constructor_defined')
      .map((col) => ({ column_name: col.column_name }));
    const columnsPerProcedure = {};
    realProcedures.forEach((proc) => {
      const procedureColumns = group.columns
        .filter((col) => col.procedure_name === proc)
        .map((col) => col.column_name);
      const allColumns = [
        ...new Set(["order_id", ...procedureColumns, ...constructorCols.map(c => c.column_name)])
      ];
      columnsPerProcedure[proc] = allColumns;
    });
    realProcedures.forEach((proc) => {
      dispatch(
        setSelectedColumns({
          procedure: proc,
          columns: columnsPerProcedure[proc],
        })
      );
    });
    // Aquí se utiliza la función executeSavedGroup definida localmente
    executeSavedGroup(realProcedures, group.database, group);
  };

  /**
   * Guardar la configuración (grupo) con las columnas seleccionadas.
   * Se asegura que se mantengan las columnas por source.
   */
  const handleSaveColumns = () => {
    if (!selectedProcedures.length) {
      alert('Seleccione al menos un procedimiento.');
      return;
    }
    for (const proc of selectedProcedures) {
      if (!selectedColumns[proc] || selectedColumns[proc].length === 0) {
        alert(`Seleccione al menos una columna para el procedimiento ${proc}.`);
        return;
      }
    }
    let paColumns = [];
    selectedProcedures.forEach((proc) => {
      const cols = [...new Set(["order_id", ...(selectedColumns[proc] || [])])].map((col) => ({
        procedure_name: proc,
        column_name: col,
        source: "user_defined",
      }));
      paColumns = paColumns.concat(cols);
    });
    let constructorColumns = [];
    if (editingProcedureId) {
      const existingGroup = savedTables.find(
        (group) => group.unique_key === editingProcedureId
      );
      if (existingGroup) {
        constructorColumns = (existingGroup.columns || []).filter(
          (col) => col.procedure_name === "constructor_defined"
        );
      }
    }
    const finalColumns = paColumns.concat(constructorColumns);
    const payload = {
      unique_key: editingProcedureId || uuidv4(),
      database: selectedDatabase,
      procedures: selectedProcedures,
      columns: finalColumns,
      group_name: selectionName?.trim() || 'Sin Nombre',
      parameters: {
        fechaInicio: '2024-04-01',
        fechaFin: '2100-12-31',
        perPage: 10,
        page: 1,
        p_column_filter: '',
        p_field_filter: ''
      },
      connectionInfo: connectionInfo,
      unique_row_id: uniqueRowId,
    };
    dispatch(saveColumns(payload))
      .unwrap()
      .then((response) => {
        alert(response.message || 'Configuración guardada exitosamente.');
        dispatch(fetchSavedTables());
        setIsModalOpen(false);
        setSelectionName('');
        setEditingProcedureId(payload.unique_key);
      })
      .catch((error) => {
        console.error('Error al guardar columnas:', error);
        alert(`Hubo un problema al guardar las columnas: ${error}`);
      });
  };

  /**
   * Guardar columnas desde el modal.
   */
  const handleModalSave = () => {
    if (!selectionName.trim()) {
      alert('Por favor, ingrese un nombre para la selección.');
      return;
    }
    handleSaveColumns();
  };

  /**
   * Cerrar el modal.
   */
  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectionName('');
  };

  /**
   * Cargar la lista de grupos guardados.
   */
  useEffect(() => {
    dispatch(fetchSavedTables());
  }, [dispatch]);

  /**
   * Eliminar un grupo guardado por su uniqueKey.
   */
  const handleDeleteProcedure = (uniqueKeyToDelete) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar el procedimiento guardado?')) {
      dispatch(deleteSavedTable(uniqueKeyToDelete))
        .unwrap()
        .then(() => {
          axios
            .delete(buildApiUrl('/delete_table/' + uniqueKeyToDelete), {
              headers: { 'X-WP-Nonce': autocrm_globals.nonce },
            })
            .then(() => {
              alert('Procedimiento y todas sus configuraciones eliminados con éxito.');
              if (uniqueKeyToDelete === editingProcedureId) {
                setSelectedDatabase('');
                setSelectedProcedures([]);
                dispatch(setSelectedColumns({}));
                dispatch(setData({}));
                dispatch(
                  setParameters({
                    fechaInicio: '2024-04-01',
                    fechaFin: '2100-12-31',
                    perPage: 10,
                    page: 1,
                    p_column_filter: '',
                    p_field_filter: ''
                  })
                );
                setEditingProcedureId(null);
                setSelectionName('');
                setConnectionInfo({});
                setUniqueRowId('');
              }
              dispatch(fetchSavedTables());
            })
            .catch((error) => {
              console.error('Error al eliminar el grupo completo:', error);
              alert(`Error al eliminar el grupo completo: ${error}`);
            });
        })
        .catch((error) => {
          console.error('Error al eliminar el procedimiento:', error);
          alert(`Error al eliminar el procedimiento: ${error}`);
        });
    }
  };

  /**
   * Exportar todos los grupos guardados en JSON.
   */
  const exportSavedProceduresToJSON = () => {
    return JSON.stringify(savedTables, null, 2);
  };

  /**
   * Importar tablas desde un archivo JSON.
   */
  const handleImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      let importedData;
      try {
        importedData = JSON.parse(text);
      } catch (parseError) {
        console.error('Error al parsear el archivo JSON:', parseError);
        alert('El archivo importado no es un JSON válido.');
        return;
      }
      dispatch(importarTablas(importedData))
        .unwrap()
        .then(() => {
          alert('Datos importados correctamente.');
          dispatch(fetchSavedTables());
        })
        .catch((error) => {
          console.error('Error al importar datos:', error);
          alert(`Error al importar datos: ${error}`);
        });
    };
    reader.readAsText(file);
  };

  return (
    <div className="autocrm-dashboard-container">
      {/* Modal para guardar la selección de columnas */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={handleModalClose}
        contentLabel="Guardar Selección de Columnas"
        className="autocrm-simple-modal-content"
        overlayClassName="autocrm-simple-modal-overlay"
      >
        <h2>Guardar Selección de Columnas</h2>
        <label>
          Nombre de la Selección:
          <input
            type="text"
            value={selectionName}
            onChange={(e) => setSelectionName(e.target.value)}
          />
        </label>
        <div className="autocrm-modal-buttons">
          <button onClick={handleModalSave}>Guardar</button>
          <button onClick={handleModalClose}>Cancelar</button>
        </div>
      </Modal>
  
      <div className="autocrm-main-container">
        {/* Sección 1: Selección y Ejecución de Procedimientos */}
        <div className="autocrm-section autocrm-section-selection">
          <h1>Primera Fase: Selección y Ejecución de Procedimientos</h1>
          <div className="autocrm-select-container">
            <label>Seleccione una base de datos</label>
            <select onChange={handleDatabaseChange} value={selectedDatabase}>
              <option value="">Seleccione una base de datos</option>
              {databases.map((db, idx) => (
                <option key={idx} value={db}>
                  {db}
                </option>
              ))}
            </select>
          </div>
  
          {selectedDatabase && (
            <div className="autocrm-select-container">
              <label>Seleccione un procedimiento</label>
              <select
                onChange={(e) => setSelectedProcedure(e.target.value)}
                value={selectedProcedure}
              >
                <option value="">Seleccione un procedimiento</option>
                {procedures.map((proc, idx) => (
                  <option key={idx} value={proc}>
                    {proc}
                  </option>
                ))}
              </select>
              <button onClick={handleAddProcedure} disabled={!selectedProcedure}>
                Agregar Procedimiento
              </button>
            </div>
          )}
  
          {selectedProcedures.length > 0 && (
            <div className="autocrm-selected-procedures">
              <h3>Procedimientos Seleccionados</h3>
              <ul>
                {selectedProcedures.map((proc, idx) => (
                  <li key={idx}>{proc}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
  
        {/* Sección 2: Visualización de Datos */}
        <div className="autocrm-section autocrm-section-data-display">
          <h2>Datos por Procedimiento</h2>
          <div className="autocrm-toolbox">
            <button
              className="autocrm-btn-save-columns"
              onClick={() => setIsModalOpen(true)}
              aria-label="Guardar Selección de Columnas"
            >
              Guardar Selección
            </button>
          </div>
          {loading && <p className="autocrm-loading">Cargando datos...</p>}
          {error && <p className="autocrm-error-message">{error}</p>}
          {!loading &&
            selectedProcedures.map((procedure) => {
              const procedureData = data[procedure];
              return (
                <div className="autocrm-data-table-container" key={procedure}>
                  {procedureData ? (
                    <ProcedureTable
                      procedureName={procedure}
                      columns={procedureData.columns?.map((col) => col.column_name) || []}
                      data={procedureData.data || []}
                    />
                  ) : (
                    <p>Cargando datos...</p>
                  )}
                </div>
              );
            })}
        </div>
  
        {/* Sección 3: Tablas Guardadas */}
        <div className="autocrm-section autocrm-section-saved-tables">
          <h2>Tablas Guardadas</h2>
          {savedTablesLoading && <p className="autocrm-loading">Cargando tablas guardadas...</p>}
          {savedTablesError && <p className="autocrm-error-message">{savedTablesError}</p>}
          {!savedTablesLoading && savedTables.length > 0 ? (
            <div className="autocrm-data-table-container">
              <table className="autocrm-saved-tables">
                <thead>
                  <tr>
                    <th>Nombre del Grupo</th>
                    <th>Base de Datos</th>
                    <th>Procedimientos</th>
                    <th>Columnas</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {savedTables.map((group) => (
                    <tr key={group.unique_key}>
                      <td>{group.group_name || 'Sin Nombre'}</td>
                      <td>{group.database}</td>
                      <td>
                        {group.procedures.map((proc, index) => (
                          <div key={`proc-${index}`}>{proc}</div>
                        ))}
                      </td>
                      <td>
                        {group.procedures.map((proc, idx) => (
                          <div key={`proc-col-${idx}`}>
                            <strong>{proc}:</strong>
                            <ul>
                              {group.columns
                                .filter((col) => col.procedure_name === proc)
                                .map((col, colIdx) => (
                                  <li key={`col-${colIdx}`}>{col.column_name}</li>
                                ))}
                            </ul>
                          </div>
                        ))}
                      </td>
                      <td>
                        <div className="autocrm-action-buttons">
                          <button onClick={() => handleSavedProcedureClick(group)}>
                            Ejecutar
                          </button>
                          <button onClick={() => handleDeleteProcedure(group.unique_key)}>
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            !savedTablesLoading &&
            !savedTablesError && <p>No hay tablas guardadas.</p>
          )}
        </div>
  
        {/* Sección 4: Importar / Exportar */}
        <div className="autocrm-section autocrm-section-import-export">
          <h2>Importación y Exportación</h2>
          <div className="autocrm-import-export-container">
            <a
              href={`data:text/json;charset=utf-8,${encodeURIComponent(exportSavedProceduresToJSON())}`}
              download="procedures-saved-export.json"
              className="autocrm-btn-export-csv"
            >
              Exportar Datos
            </a>
            <div className="autocrm-btn-import-csv">
              <label htmlFor="file-upload">Importar Datos</label>
              <input
                id="file-upload"
                type="file"
                accept=".json"
                onChange={handleImport}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectorBD;
